import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import BBALoader from './BBALoader';

const SmartRedirect = () => {
  const { currentUser, loading: authLoading, authError } = useAuth();
  const [userRole, setUserRole] = useState(null);
  const [userStatus, setUserStatus] = useState(null);
  const [onboardingStatus, setOnboardingStatus] = useState(null);
  const [accountType, setAccountType] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userDataError, setUserDataError] = useState(null);
  const isDev = process.env.NODE_ENV === 'development';

  useEffect(() => {
    let timeoutId;
    let isMounted = true;

    const fetchUserData = async () => {
      if (currentUser) {
        try {
          // Set a timeout for user data fetching
          timeoutId = setTimeout(() => {
            if (isMounted) {
              console.warn('User data fetch timeout - redirecting to login');
              setUserDataError(new Error('User data fetch timeout'));
              setLoading(false);
            }
          }, 8000); // 8 second timeout for user data

          // Check internal_users collection first (CRM users)
          const internalUserDoc = await getDoc(doc(db, 'internal_users', currentUser.uid));
          if (internalUserDoc.exists()) {
            const data = internalUserDoc.data();
            setUserRole(data.role);
            setUserStatus(data.status);
            setOnboardingStatus(null); // Internal users don't have onboarding
          } else {
            // Check users collection (Portal users)
            const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
            if (userDoc.exists()) {
              const data = userDoc.data();
              setUserRole(data.role);
              setUserStatus(data.status);
              setOnboardingStatus(data.onboardingStatus);
              
              // If this is a portal user with a linked account, check the account type
              if (data.accountId) {
                try {
                  const accountDoc = await getDoc(doc(db, 'accounts', data.accountId));
                  if (accountDoc.exists()) {
                    const accountData = accountDoc.data();
                    setAccountType(accountData.type);
                  }
                } catch (error) {
                  console.error('Error fetching linked account:', error);
                  // If we can't fetch the account, default to null
                  setAccountType(null);
                }
              }
            }
          }
          
          if (isMounted) {
            setUserDataError(null);
            setLoading(false);
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
          if (isMounted) {
            setUserDataError(error);
            setLoading(false);
          }
        } finally {
          if (timeoutId) {
            clearTimeout(timeoutId);
          }
        }
      } else if (!authLoading) {
        // If auth is done loading and no user, stop loading
        setLoading(false);
      }
    };

    // Only fetch user data if auth is not loading and we have a current user
    if (!authLoading && currentUser) {
      fetchUserData();
    } else if (!authLoading && !currentUser) {
      // If auth is done loading and no user, stop loading
      setLoading(false);
    }

    return () => {
      isMounted = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [currentUser, authLoading]);

  // In development mode, redirect to start by default
  if (isDev) {
    return <Navigate to="/start" replace />;
  }

  // If there's an auth error, redirect to login
  if (authError) {
    console.error('Auth error in SmartRedirect:', authError);
    return <Navigate to="/login" replace />;
  }

  // Show loading while auth is loading or fetching user data
  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <BBALoader size={128} />
      </div>
    );
  }

  // If there's an error fetching user data, redirect to login
  if (userDataError) {
    console.error('User data error in SmartRedirect:', userDataError);
    return <Navigate to="/login" replace />;
  }

  // If not authenticated, redirect to login
  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  // Redirect based on user type and role
  if (userRole === 'Owner' || userRole === 'Executive' || userRole === 'Member' || userRole === 'Contractor' || userRole === 'Accountant' || userRole === 'team') {
    // Internal users (CRM) - redirect to CRM
    return <Navigate to="/crm" replace />;
  } else if (userRole === 'client') {
    // Portal users - check linked account type to determine routing
    if (accountType === 'Clients' || accountType === 'clients') {
      // This is a client account - redirect to portal dashboard
      return <Navigate to="/portal" replace />;
    } else {
      // This is a lead account or no linked account - redirect to start dashboard
      return <Navigate to="/start" replace />;
    }
  } else {
    // Default to start for other roles or if no role is found
    return <Navigate to="/start" replace />;
  }
};

export default SmartRedirect; 